import { useSnackbar } from "notistack";
import { useState } from "react";
import { ArrowDownTrayIcon } from "components/icons/outline";
import { cn } from "lib/cn";
import { downloadFilesAsZip } from "lib/downloadZip";

type DownloadAllButtonProps = {
    /** File IDs to bundle. The button renders nothing for fewer than 2. */
    fileIds: string[];
    /** Base name for the zip (sanitized server-side). */
    archiveName?: string;
    className?: string;
    label?: string;
};

/**
 * PRTL-1350: one-click "Download all" for a message's attachments — a single
 * zip built server-side, so it works for private + public files and never
 * trips popup blockers.
 */
export const DownloadAllButton = ({
    fileIds,
    archiveName = "attachments",
    className,
    label = "Download all",
}: DownloadAllButtonProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const [busy, setBusy] = useState(false);
    if (fileIds.length < 2) return null;

    const onClick = async () => {
        setBusy(true);
        try {
            await downloadFilesAsZip(fileIds, archiveName);
        } catch (err) {
            enqueueSnackbar(
                err instanceof Error && err.message
                    ? err.message
                    : "Couldn't download attachments.",
                { variant: "error" },
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <button
            type="button"
            onClick={() => void onClick()}
            disabled={busy}
            className={cn(
                "inline-flex items-center gap-1 self-start rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50",
                className,
            )}
        >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {busy ? "Preparing…" : `${label} (${fileIds.length})`}
        </button>
    );
};
